import { formatCurrency } from '../utils/numberFormat';
import { Currency, Pager } from '../components/ui/DataPrimitives';
import { PageShell, DataTable } from '../components/ui/DataPrimitives';
import SupplierSalesSection from '../components/SupplierSalesSection';
import { chartTimeData, chartTimeAxis, chartTicks, chartAxis, chartGrid, chartTooltipStyle, chartPalette, chartMonth, chartMoney } from '../components/charts/SalesChartPrimitives';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { Modal, LoadingSpinner } from '../components/SharedComponents';
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ResponsiveContainer } from 'recharts';
import PerformanceTab from '../components/performance/PerformanceTab';
import { getGradeColor } from '../components/performance/gradeColors';
import LeadtimeReliabilityPanel from '../components/LeadtimeReliabilityPanel';
import DeliveryReliabilityPanel from '../components/DeliveryReliabilityPanel';
import InventoryTab from '../components/inventory/InventoryTab';
import MetricInfo from '../components/MetricInfo';
import MarketBlendLabel from '../components/MarketBlendLabel';
import AnomalyExclusionTag from '../components/AnomalyExclusionTag';
import MarkerLegend from '../components/MarkerLegend';
import PriceSummaryBand from '../components/PriceSummaryBand';
import SupplierOnRecord from '../components/SupplierOnRecord';
import {
  priceChangeStatusMessage,
  priceChangeAbsence,
  priceChangeLowCoverageLine,
  partIndexCoverageLine,
} from '../utils/priceChangeStatus';
import ForwardRunRateLine from '../components/ForwardRunRateLine';
import ForwardExposureLine from '../components/ForwardExposureLine';
import PpiInsufficientBanner from '../components/PpiInsufficientBanner';
import { pluralize } from '../utils/pluralize';
// Lane 940 -- the ONE date-only parser this repo already has. A bare
// `new Date('2026-06-01')` is parsed as UTC MIDNIGHT and then rendered in LOCAL
// time, so west of UTC every month label on the Price-vs-Market chart came out
// one month early: the June point read "May 26". Measured, not reasoned --
// `backend/scripts/lane940_ppi_chart_freshness_probe.py` proved the engine
// returns 2026-06 at 6/12/24 months for all 124 profiled suppliers, so the
// month was never missing from the data, only from the label. `toLocalEpoch`
// already handles the bare `YYYY-MM` shape and already carries this exact
// caution in its docstring (utils/opportunityChart.js:30-36); the Opportunity
// price chart has used it since Lane 47. Do NOT reintroduce `new Date(m + '-01')`.
import { toLocalEpoch } from '../utils/opportunityChart';
// Lane 138: single-sourced with the band tile so the panel's window-label
// disclosure and the tile's note can never drift apart.
import { formatPriorWindowGap } from '../utils/priceSectionNotes';
import { API_BASE } from '../config';
// Lane 198 (Linear rebuild, Stage 2): page-scoped light-only token layer.
import './SupplierDetail.linear.css';

function SupplierDetail() {
  const { supplierName } = useParams();
  const navigate = useNavigate();
  // scorecardUiEnabled (§5.BT §3.3 Commit C, 2026-06-19) gates the Performance
  // surface -- Layer 3 of the three-layer PROD-invisibility defense (P2 §A).
  // inventoryUiEnabled (§5.BU §I.3 Commit C, 2026-06-19) gates the Inventory
  // surface via the same three-layer pattern (P2 §A + Commit A backend gate).
  const { token, scorecardUiEnabled, inventoryUiEnabled, deliveryReliabilityEnabled, supplierDecisionsEnabled } = useAuth();

  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Lane 198: the de-tabbed Linear layout promotes Overview + Spend & Materials
  // to the main scroll; the deeper tabs (Research / Performance / Inventory /
  // Contracts / Account context / Notes) become "Explore" chip targets revealed
  // one at a time. `activeExplore` is the open chip key (null = none open).
  const [activeExplore, setActiveExplore] = useState(null);

  // Contract modal state
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [contractModalData, setContractModalData] = useState(null);
  const [contractModalLoading, setContractModalLoading] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);

  // Supplier group context (A2)
  const [groupContext, setGroupContext] = useState(null);



  // Explore: Research
  const [researchData, setResearchData] = useState(null);
  const [researchLoading, setResearchLoading] = useState(false);
  const [researchLoaded, setResearchLoaded] = useState(false);

  // Spend & Materials -- pagination
  const [spendTimeframe, setSpendTimeframe] = useState(12);
  const [materialsPage, setMaterialsPage] = useState(1);
  const [materialsSearch, setMaterialsSearch] = useState('');
  const [materialsSortBy, setMaterialsSortBy] = useState('spend');

  // Spend & Materials: YoY Price Impact (Feature 18)
  const [priceImpact, setPriceImpact] = useState(null);
  const [priceImpactLoading, setPriceImpactLoading] = useState(false);
  const [priceImpactMonths, setPriceImpactMonths] = useState(12);
  // Lane 138: the window the CURRENTLY-HELD priceImpact response represents.
  // Distinct from priceImpactMonths, which flips the instant a selector button
  // is clicked while the fetch is still in flight. The band labels its dollar
  // tile from THIS, so the label and the number always describe the same
  // response.
  const [priceImpactWindow, setPriceImpactWindow] = useState(null);
  const [priceImpactExpanded, setPriceImpactExpanded] = useState(true);
  // Round 4 (D3): the ONE owner-driven insufficiency message. When it exists,
  // headline claims are withheld on BOTH consumers (band tile and YoY header)
  // while per-material rows stay.
  const yoyInsufficientMessage = priceChangeStatusMessage(
    priceImpact?.price_change_status, priceImpactWindow);
  const [ppiChartMode, setPpiChartMode] = useState('blended'); // 'blended' | 'category'
  const [ppiPeriods, setPpiPeriods] = useState({}); // {6: ppi_blend, 12: ppi_blend, 24: ppi_blend}
  // Lane 1414: each window's `price_change_status`, from the SAME response as
  // its blend above. It says whether the price side is low coverage or absent.
  const [ppiStatuses, setPpiStatuses] = useState({}); // {6: status, 12: status, 24: status}
  // Lane 713 (D50.4): the dossier's ONE forward number, on Ryan's ladder
  // formula. Read from the months=12 response ONLY -- the ladder is a
  // 12-month-only quantity, and sourcing it from the selector-driven fetch as
  // well would give one figure two provenances that drift the moment the
  // selector moves. Null whenever DOSSIER_FORWARD_LADDER_ENABLED is off.
  const [forwardExposure, setForwardExposure] = useState(null);
  const [ppiPeriodsLoaded, setPpiPeriodsLoaded] = useState(false);
  const [outlierFootnoteOpen, setOutlierFootnoteOpen] = useState(false);
  // Lane 226: YoY table sort by Impact (signed, both directions -- the analytics
  // convention). null = the response's default order (top-20 by |impact|, i.e.
  // today's view); first click -> 'desc' (biggest increases first); then toggles
  // 'desc' <-> 'asc' (biggest decreases first).
  const [yoyImpactSortDir, setYoyImpactSortDir] = useState(null);

  // Lane 134: price summary band (spec: docs/design/supplier-dossier-price-
  // section-spec.md). Pinned to the 12- and 24-month responses regardless of
  // the panel's window selector; captured from the same fetchPpiAllPeriods
  // round-trip the card already makes.
  const [priceBand, setPriceBand] = useState(null); // {data12, blend24}
  const [scorecardSummary, setScorecardSummary] = useState(null);
  const [scorecardSummaryLoaded, setScorecardSummaryLoaded] = useState(false);

  // QA-3 items 4+5 (Mega QA 2026-07-22): the delivery-reliability summary,
  // fetched proactively on mount (mirrors fetchScorecardSummary) -- NOT lazily
  // via openExplore('delivery') -- because it feeds the TOP figure-strip card
  // (item 4) and the Performance-tab callout (item 5), both of which must be
  // populated before the user ever opens the "Delivery" chip. Double OFF-lock:
  // gated on deliveryReliabilityEnabled first (no fetch when off), mirroring
  // DeliveryReliabilityPanel's own gate.
  const [deliverySummary, setDeliverySummary] = useState(null);
  const [deliverySummaryLoaded, setDeliverySummaryLoaded] = useState(false);

  // Explore: Account Context (interview intelligence)
  const [interviewIntel, setInterviewIntel] = useState(null);
  const [interviewIntelLoading, setInterviewIntelLoading] = useState(false);
  const [interviewIntelLoaded, setInterviewIntelLoaded] = useState(false);
  // Lane 1342: the supplier's notices + the team's standing decisions.
  // { name, data, failed }: every result carries the supplier it is for, and
  // it renders only while that is still the page's supplier, so navigation can
  // never show another supplier's records, not even for one render.
  const [onRecordState, setOnRecordState] = useState(null);
  // The supplier with a request in flight or already loaded. Cleared on a
  // failure so reopening Team knowledge retries (Codex review).
  const onRecordFor = useRef(null);

  // Explore: Notes
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Explore: Performance (§5.BT §3.3 Commit C) -- lazy-resolved supplier_id.
  const [performanceSupplierId, setPerformanceSupplierId] = useState(null);
  const [performanceResolved, setPerformanceResolved] = useState(false);

  // Explore: Inventory (§5.BU §I.3 Commit C) -- lazy-resolved supplier_id.
  const [inventorySupplierId, setInventorySupplierId] = useState(null);
  const [inventoryResolved, setInventoryResolved] = useState(false);

  // Explore: Delivery reliability (Lane 295) -- lazy-resolved supplier_id,
  // mirrors Inventory's resolution (reuses a resolved id if Performance /
  // Inventory already fetched one, else resolves by name on chip open).
  const [deliverySupplierId, setDeliverySupplierId] = useState(null);
  const [deliveryResolved, setDeliveryResolved] = useState(false);

  // Shared helper: resolve supplier name -> supplier_id via buyer's supplier list
  const resolveSupplierIdByName = useCallback(async () => {
    if (!token || !supplierName) return null;
    try {
      const searchRes = await fetch(
        `${API_BASE}/api/company/suppliers?search=${encodeURIComponent(supplierName)}&page_size=5`,
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (!searchRes.ok) return null;
      const searchData = await searchRes.json();
      const match = (searchData.suppliers || []).find(
        s => s.supplier_name.toLowerCase() === decodeURIComponent(supplierName).toLowerCase()
      );
      return match?.supplier_id || null;
    } catch (err) {
      console.log('Supplier ID resolution failed:', err.message);
      return null;
    }
  }, [token, supplierName]);

  useEffect(() => {
    if (supplierName) {
      fetchSupplierDetail();
      fetchGroupContext();
      // Lane 198: Spend & Materials is now inline on the main scroll (not a
      // lazy tab), so its data loads on mount. Each fetch is idempotent /
      // self-guarding, so a supplier change re-fetches exactly once.
      fetchPriceImpact(priceImpactMonths);
      fetchPpiAllPeriods();
      fetchScorecardSummary();
      fetchDeliverySummary();
    }
  }, [supplierName, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSupplierDetail = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE}/api/supplier-hub/${encodeURIComponent(supplierName)}/detail`,
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (response.ok) {
        setSupplier(await response.json());
      } else {
        setError('Failed to load supplier details');
      }
    } catch (err) {
      console.error('Error fetching supplier:', err);
      setError('Failed to load supplier details');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupContext = async () => {
    const supplierId = await resolveSupplierIdByName();
    if (!supplierId) return;
    try {
      const res = await fetch(`${API_BASE}/api/suppliers/${supplierId}/group-context`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.group) setGroupContext(data);
      }
    } catch (err) {
      console.log('Group context error:', err.message);
    }
  };



  const fetchResearchData = async () => {
    if (researchLoaded) return;
    const supplierId = await resolveSupplierIdByName();
    if (!supplierId) { setResearchLoaded(true); return; }
    setResearchLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/suppliers/${supplierId}/research`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.research_summary || data.research_data) setResearchData(data);
      }
    } catch (err) {
      console.log('Research data error:', err.message);
    } finally {
      setResearchLoading(false);
      setResearchLoaded(true);
    }
  };

  const fetchPriceImpact = async (months) => {
    if (!token || !supplierName) return;
    setPriceImpactLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/analytics/supplier-price-impact/${encodeURIComponent(supplierName)}?months=${months}`,
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (res.ok) {
        setPriceImpact(await res.json());
        // Lane 138: set together with the response so the pair can never drift.
        setPriceImpactWindow(months);
      }
    } catch (err) {
      console.log('Price impact fetch failed:', err.message);
    } finally {
      setPriceImpactLoading(false);
    }
  };

  const handlePriceImpactMonthsChange = (newMonths) => {
    setPriceImpactMonths(newMonths);
    fetchPriceImpact(newMonths);
  };

  // Lane 226: cycle the YoY Impact sort. Untouched (null) preserves the
  // response's default order; the first click sorts biggest increases first
  // (desc, the analytics impact-column default), then toggles desc <-> asc.
  const toggleYoyImpactSort = () => {
    setYoyImpactSortDir(prev => (prev === 'desc' ? 'asc' : 'desc'));
  };

  const fetchPpiAllPeriods = async () => {
    if (!token || !supplierName || ppiPeriodsLoaded) return;
    setPpiPeriodsLoaded(true);
    try {
      const results = await Promise.all(
        [6, 12, 24].map(m =>
          fetch(
            `${API_BASE}/api/analytics/supplier-price-impact/${encodeURIComponent(supplierName)}?months=${m}`,
            { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
          ).then(r => r.ok ? r.json() : null).catch(() => null)
        )
      );
      const periods = {};
      const statuses = {};
      [6, 12, 24].forEach((m, i) => {
        if (results[i]?.ppi_blend) periods[m] = results[i].ppi_blend;
        if (results[i]?.price_change_status) statuses[m] = results[i].price_change_status;
      });
      setPpiPeriods(periods);
      setPpiStatuses(statuses);
      // Lane 713 (D50.4): results[1] IS the months=12 response -- the same
      // object the band reads below -- so the forward figure and the 12-month
      // blend it sits under can never come from two different fetches.
      setForwardExposure(results[1]?.forward_exposure || null);
      // Lane 134: the summary band reads the FULL 12-month response (summary
      // echoes + blend) and the 24-month blend -- already fetched above.
      setPriceBand({
        data12: results[1] || null,
        blend24: results[2]?.ppi_blend || null,
        status24: results[2]?.price_change_status || null,
      });
    } catch (err) {
      console.log('PPI periods fetch failed:', err.message);
    }
  };

  // Lane 134: latest scorecard quarter for the band's "From the quarterly
  // scorecard" tile. Gated on scorecardUiEnabled exactly like Performance
  // (§5.BT three-layer invisibility): flag OFF -> tile ABSENT, no fetch.
  const fetchScorecardSummary = async () => {
    if (scorecardSummaryLoaded) return;
    setScorecardSummaryLoaded(true);
    if (!scorecardUiEnabled) {
      setScorecardSummary({ state: 'hidden' });
      return;
    }
    setScorecardSummary({ state: 'loading' });
    try {
      const supplierId = await resolveSupplierIdByName();
      if (!supplierId) { setScorecardSummary({ state: 'none' }); return; }
      const res = await fetch(
        `${API_BASE}/api/suppliers/${encodeURIComponent(supplierId)}/scorecard?quarters=1`,
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (!res.ok) { setScorecardSummary({ state: 'none' }); return; }
      const data = await res.json();
      const latest = data?.has_data && data.quarters?.length ? data.quarters[0] : null;
      const agg = latest?.aggregated;
      // QA2-1 (Lane 241): the top "Overall grade" figure card reuses the SAME
      // aggregated worst-of grade the Performance section shows
      // (agg.overall_rating -> GradeTrajectoryStrip). Carried on the summary in
      // both branches so a supplier with a grade but no scorecard price figure
      // still surfaces it; null when ungraded (honest empty). No new math.
      const grade = agg?.overall_rating || null;
      if (agg && agg.src_price_change_pct !== null && agg.src_price_change_pct !== undefined) {
        setScorecardSummary({
          state: 'ready',
          pct: Number(agg.src_price_change_pct),
          quarterLabel: latest.period_label || null,
          status: agg.wm_yoy_reconciliation_status || null,
          grade,
        });
      } else {
        setScorecardSummary({ state: 'none', grade });
      }
    } catch (err) {
      console.log('Scorecard summary fetch failed:', err.message);
      setScorecardSummary({ state: 'none' });
    }
  };

  // QA-3 items 4+5: proactive delivery-reliability fetch. Skips entirely (no
  // network call) when the flag is off -- the double OFF-lock -- so a flag-OFF
  // dossier is byte-identical to today (no new request fires at all).
  const fetchDeliverySummary = async () => {
    if (deliverySummaryLoaded) return;
    setDeliverySummaryLoaded(true);
    if (!deliveryReliabilityEnabled) return;
    try {
      const supplierId = await resolveSupplierIdByName();
      if (!supplierId) return;
      const res = await fetch(
        `${API_BASE}/api/suppliers/${supplierId}/delivery-reliability`,
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (!res.ok) return;
      const data = await res.json();
      if (data?.delivery_reliability_enabled) setDeliverySummary(data);
    } catch (err) {
      console.log('Delivery reliability summary fetch failed:', err.message);
    }
  };

  const fetchOnRecord = async () => {
    const forName = supplierName;
    if (!supplierDecisionsEnabled || !forName || onRecordFor.current === forName) return;
    onRecordFor.current = forName;
    const settle = (data, failed) => {
      if (onRecordFor.current !== forName) return; // a later supplier won
      if (failed) onRecordFor.current = null;       // allow a retry
      setOnRecordState({ name: forName, data, failed });
    };
    try {
      const supplierId = await resolveSupplierIdByName();
      if (!supplierId) {
        // Not resolved (lookup failed or no match): show nothing, and let a
        // later open of Team knowledge try again.
        if (onRecordFor.current === forName) onRecordFor.current = null;
        return;
      }
      const res = await fetch(`${API_BASE}/api/suppliers/${supplierId}/on-record`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (res.ok) settle(await res.json(), false);
      else settle(null, res.status !== 404);
    } catch (err) {
      console.log('Supplier on-record fetch failed:', err.message);
      settle(null, true);
    }
  };

  useEffect(() => {
    // A different supplier: drop the previous supplier's records, and refetch
    // if Team knowledge is already open.
    onRecordFor.current = null;
    if (activeExplore === 'account_context') fetchOnRecord();
  }, [supplierName]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchInterviewIntelligence = async () => {
    if (interviewIntelLoaded) return;
    const supplierId = await resolveSupplierIdByName();
    if (!supplierId) { setInterviewIntelLoaded(true); return; }
    setInterviewIntelLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/suppliers/${supplierId}/interview-intelligence`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        const data = await res.json();
        setInterviewIntel(data);
      }
    } catch (err) {
      console.log('Interview intelligence fetch failed:', err.message);
    } finally {
      setInterviewIntelLoading(false);
      setInterviewIntelLoaded(true);
    }
  };

  const openContractModal = async (contract) => {
    setSelectedContract(contract);
    setContractModalOpen(true);
    setContractModalLoading(true);
    setContractModalData(null);
    try {
      const res = await fetch(`${API_BASE}/api/contracts/${contract.contract_id}/parsed`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.parsed) setContractModalData(data.terms);
      }
    } catch (err) {
      console.error('Error loading contract details:', err);
    } finally {
      setContractModalLoading(false);
    }
  };

  const parseClauses = (clauses) => {
    if (!clauses) return {};
    if (typeof clauses === 'string') {
      try { return JSON.parse(clauses); } catch { return {}; }
    }
    return clauses;
  };

  const getDaysUntilExpiry = (date) => {
    if (!date) return null;
    const expiry = new Date(date);
    const today = new Date();
    return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not available';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getQualityColor = (rating) => {
    if (!rating) return 'var(--text-3)';
    if (rating >= 80) return 'var(--good)';
    if (rating >= 60) return 'var(--warn)';
    return 'var(--crit)';
  };

  const getQualityLabel = (rating) => {
    if (!rating) return 'N/A';
    if (rating >= 80) return 'Good';
    if (rating >= 60) return 'Fair';
    return 'Poor';
  };

  // Add a supplier note
  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setSavingNote(true);
    try {
      const res = await fetch(`${API_BASE}/api/suppliers/${encodeURIComponent(supplierName)}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify({ note_text: newNote, note_type: 'general' }),
      });
      if (res.ok) {
        setNewNote('');
        fetchSupplierDetail(); // refresh to pull the new note into supplier.notes
      }
    } catch (error) {
      console.error('Error adding note:', error);
    } finally {
      setSavingNote(false);
    }
  };

  // Lane 198: open (or toggle closed) an Explore section, lazy-loading its data
  // the first time it opens. Mirrors the old handleTabChange lazy-load rules.
  const openExplore = (key) => {
    const next = activeExplore === key ? null : key;
    setActiveExplore(next);
    if (next === 'research' && !researchLoaded) fetchResearchData();
    if (next === 'account_context' && !interviewIntelLoaded) fetchInterviewIntelligence();
    if (next === 'account_context') fetchOnRecord();
    if (next === 'performance' && !performanceResolved) {
      resolveSupplierIdByName().then((id) => {
        if (id) setPerformanceSupplierId(id);
        setPerformanceResolved(true);
      });
    }
    if (next === 'inventory' && !inventoryResolved) {
      // Reuse the Performance-resolved id if present to avoid a duplicate
      // /api/company/suppliers?search= round-trip.
      if (performanceResolved && performanceSupplierId) {
        setInventorySupplierId(performanceSupplierId);
        setInventoryResolved(true);
      } else {
        resolveSupplierIdByName().then((id) => {
          if (id) setInventorySupplierId(id);
          setInventoryResolved(true);
        });
      }
    }
    if (next === 'delivery' && !deliveryResolved) {
      // Reuse an already-resolved id (Performance or Inventory) to avoid a
      // duplicate /api/company/suppliers?search= round-trip.
      if (performanceResolved && performanceSupplierId) {
        setDeliverySupplierId(performanceSupplierId);
        setDeliveryResolved(true);
      } else if (inventoryResolved && inventorySupplierId) {
        setDeliverySupplierId(inventorySupplierId);
        setDeliveryResolved(true);
      } else {
        resolveSupplierIdByName().then((id) => {
          if (id) setDeliverySupplierId(id);
          setDeliveryResolved(true);
        });
      }
    }
  };

  if (loading) {
    return (
      <PageShell className="sd">
        <div style={{ padding: '48px' }}><LoadingSpinner text="Loading supplier details..." /></div>
      </PageShell>
    );
  }

  if (error || !supplier) {
    return (
      <PageShell className="sd">
        <div className="sd-wrap">
          <div className="sd-empty" style={{ marginTop: '40px' }}>
            <div style={{ color: 'var(--crit)', marginBottom: '16px' }}>{error || 'Supplier not found'}</div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button className="sd-btn" onClick={() => navigate('/suppliers')}>{'←'} Back to Suppliers</button>
              {error && <button className="sd-btn pri" onClick={fetchSupplierDetail}>Try again</button>}
            </div>
          </div>
        </div>
      </PageShell>
    );
  }

  // ---- Derived data ----

  const categories = supplier.summary.categories || [];

  // Group banner facts
  let groupInfo = null;
  if (groupContext && groupContext.group) {
    const group = groupContext.group;
    const thisSpend = (group.members || []).find(m => m.supplier_id === groupContext.supplier_id)?.spend ?? null;
    const pct = group.consolidated_spend > 0 ? ((thisSpend / group.consolidated_spend) * 100).toFixed(0) : 0;
    const siblings = (group.members || []).filter(m => m.supplier_id !== groupContext.supplier_id);
    groupInfo = { group, thisSpend, pct, siblings };
  }

  // Figure-strip derived values
  const contractsList = supplier.contracts || [];
  const upcomingDays = contractsList.map(c => c.days_until_expiration).filter(d => d != null && d > 0);
  const soonestDays = upcomingDays.length ? Math.min(...upcomingDays) : null;

  // Materials filtering / sorting / pagination
  const allMaterials = supplier.materials || [];
  const filteredMaterials = allMaterials.filter(mat => {
    if (!materialsSearch) return true;
    const s = materialsSearch.toLowerCase();
    return (mat.material_number || '').toLowerCase().includes(s) ||
           (mat.material_name || '').toLowerCase().includes(s);
  });
  const sortedMaterials = [...filteredMaterials].sort((a, b) => {
    if (materialsSortBy === 'spend') return (b.spend_12mo || 0) - (a.spend_12mo || 0);
    if (materialsSortBy === 'last_purchase') return new Date(b.last_activity || 0) - new Date(a.last_activity || 0);
    if (materialsSortBy === 'material_number') return (a.material_number || '').localeCompare(b.material_number || '');
    return 0;
  });
  const PAGE_SIZE = 50;
  const totalPages = Math.max(1, Math.ceil(sortedMaterials.length / PAGE_SIZE));
  const pagedMaterials = sortedMaterials.slice((materialsPage - 1) * PAGE_SIZE, materialsPage * PAGE_SIZE);

  // Lane 226: the YoY table's shown rows. When the Impact header is engaged,
  // sort the FULL list by signed dollar_impact then take the top 20, so "asc"
  // genuinely surfaces the biggest decreases (which may not be in the response's
  // top-20-by-magnitude). Matched ('both') rows carry an impact; new /
  // discontinued rows have no comparison and always sort last. Untouched
  // (null) preserves the response's default order (today's view).
  const yoyRows = (() => {
    const mats = priceImpact?.materials || [];
    if (!yoyImpactSortDir) return mats.slice(0, 20);
    return [...mats].sort((a, b) => {
      const aHas = a.status === 'both';
      const bHas = b.status === 'both';
      if (aHas !== bHas) return aHas ? -1 : 1;
      const av = a.dollar_impact || 0;
      const bv = b.dollar_impact || 0;
      return yoyImpactSortDir === 'asc' ? av - bv : bv - av;
    }).slice(0, 20);
  })();

  // Explore chip definitions (flag-gated ones only appear when enabled).
  // QA2-1 (Lane 241): the per-item hint subtexts ("grade · KPIs" / "stock ·
  // OSMI" / the count/confidence descriptors) are dropped -- the nav reads as
  // plain section labels. Sections still reveal inline; only the descriptor
  // text goes.
  const exploreChips = [
    // Lane 295: the flagship "prominent and unmistakable / permeate everywhere"
    // dimension (plan lock 2) LEADS the section nav, gated ONLY on
    // DELIVERY_RELIABILITY_ENABLED (NOT scorecard UI — it is independent of
    // scorecards). Flag OFF -> filtered out, so the nav starts with Performance
    // exactly as before (byte-identical on PROD today).
    { key: 'delivery', label: 'Delivery', show: deliveryReliabilityEnabled },
    { key: 'performance', label: 'Performance', show: scorecardUiEnabled },
    { key: 'inventory', label: 'Inventory', show: inventoryUiEnabled },
    { key: 'contracts', label: 'Contracts', show: true },
    { key: 'research', label: 'Research', show: true },
    { key: 'customers', label: 'Customers', show: true },
    { key: 'account_context', label: 'Team knowledge', show: true },
  ].filter(c => c.show);

  // Lane 718 (Ryan's D52.2): ONE resolver for "which blend is window N showing",
  // so the panel-level banner and the period tiles below it read the identical
  // object. The tiles used to inline this expression; a banner that inlined its
  // own copy could say "no comparison for this supplier" over a tile rendering a
  // gap, which is the self-contradiction the banner exists to prevent.
  const ppiBlendForWindow = (period) =>
    ppiPeriods[period] || (period === priceImpactMonths ? priceImpact?.ppi_blend : null);
  // Lane 1414: the status that came WITH that blend, so a tile never pairs one
  // response's figures with another response's flag.
  const ppiStatusForWindow = (period) =>
    ppiPeriods[period]
      ? (ppiStatuses[period] || null)
      : (period === priceImpactMonths ? priceImpact?.price_change_status || null : null);
  // Lane 1414: the server's low-coverage switch is on (the status says so).
  const categoryMarketOnly = priceImpact?.price_change_status?.low_coverage_enabled === true;

  // ⭐ Lane 950 / Ryan's D53 (2026-08-08): THE GEOGRAPHY MISMATCH IS NAMED, NOT
  // HIDDEN -- on the page Ryan opens daily.
  //
  //   "Just have it call it out. Just be like, by the way, our intelligence says
  //    these guys are... this company is German. This is an American index. It's
  //    still applicable if you're using an American entity of this company."
  //
  // THE DEFECT THIS CLOSES. Lane 942 measured chat REFUSING the market figure for
  // Freudenberg in writing while this panel printed +15.06% / +3.25% / +11.81
  // anyway, because the Lane 759 geography guard was never added here (Lane 937's
  // D-NEW-1). Ryan's D46 then settled which of the two answers wins -- "the fix
  // is to stop suppressing, not to add the guard to the dossier" -- and D53 is
  // how it stops suppressing: the figure stays and the caveat goes beside it.
  //
  // ⛔ THE SENTENCE IS NOT WRITTEN HERE, AND MUST NEVER BE (Rule 120
  // consequence 1). It comes from the server, out of the ONE producer
  // `helpers/chat_benchmark_geography.geo_caveat_clause` -- the same single
  // source chat and the deck render -- so a buyer who asks the same question on
  // three surfaces gets one account of what the comparison is worth. There is
  // deliberately NO client-side geography logic: the classifier needs the
  // supplier's recorded HQ, the region map and the region of every series on the
  // profile, and a JS re-derivation of that is exactly the second engine Rule 120
  // forbids.
  //
  // ⚠️ NO FIGURES => NO CAVEAT (Lane 947's own rule). A naked geography
  // disclosure over three "Insufficient purchase history" tiles caveats nothing
  // and reads as a second, unrelated silence -- the `PpiInsufficientBanner`
  // failure mode in a new costume. So this renders only when at least one arrived
  // window is showing a real comparison.
  //
  // ⛔⛔ INERT UNTIL THE SERVER SENDS THE FIELD. `ppi_geo_caveat` is NOT on the
  // `/api/analytics/supplier-price-impact` response today; `routes/analytics.py`
  // is outside this lane's file set and owes exactly one key (reported to the
  // coordinator). Absent field -> `null` -> this panel is byte-identical to
  // today, geography-mismatched suppliers included.
  const ppiGeoCaveat = (() => {
    const text = priceImpact?.ppi_geo_caveat;
    if (typeof text !== 'string' || !text.trim()) return null;
    const arrived = [6, 12, 24].map(ppiBlendForWindow).filter(Boolean);
    if (!arrived.some(b => b.confidence !== 'insufficient')) return null;
    return text.trim();
  })();

  return (
    <PageShell className="sd">
      {/* Top bar / breadcrumb */}
      <div className="sd-topbar">
        <div className="sd-crumb">
          <button onClick={() => navigate('/suppliers')}>Suppliers</button>
          <span className="sep">/</span>
          <span className="cur">{supplier.supplier_name}</span>
        </div>
      </div>

      <div className="sd-wrap">
        {/* Header */}
        {/* QA R2-B (Mega QA 2 2026-07-22): the initials avatar square is removed
            -- the supplier name sits flush left, no decorative box. */}
        <header className="sd-head">
          <div className="sd-idcol">
            <div className="sd-nmeta">
              <h1 className="sd-name">{supplier.supplier_name} <span className="sales-header-id">{supplier.supplier_id}</span></h1>
            </div>
            {categories.length > 0 && (
              <div className="sd-cats">
                {categories.map((cat, i) => (
                  <React.Fragment key={cat}>{i > 0 && <span className="dot">&middot;</span>}{cat}</React.Fragment>
                ))}
              </div>
            )}
            {/* Lane 382 item 6: the group line rolls its dollars up to compact
                millions/thousands instead of spelling every digit. Ryan (staging
                QA 2026-07-25): "don't go out to the full number, roll those up in
                millions, like 3.5 million, and then '57% of 6.2 million'". This
                is a HEADER orientation line -- four full-precision figures on one
                row is what made it unreadable; the exact dollars live on the
                figure strip and the analytics surfaces. The sibling divisions use
                the same formatter, so the whole line reads at one scale. */}
            {groupInfo && (
              <div className="sd-group">
                Part of <b>{groupInfo.group.group_name}</b>
                {': '}<Currency value={groupInfo.thisSpend}/> ({groupInfo.pct}%)
                {' of '}<Currency value={groupInfo.group.consolidated_spend}/> group spend
                {groupInfo.siblings.length > 0 && (
                  <>
                    {' · '}Other divisions:{' '}
                    {groupInfo.siblings.map((s, i) => (
                      <span key={s.supplier_id}>
                        {i > 0 ? ', ' : ''}{s.supplier_name}
                        <span className="sd-muted"> (<Currency value={s.spend}/>)</span>
                      </span>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
          <div className="sd-actions">
            <button className="sd-btn" onClick={() => openExplore('account_context')}>Add note</button>
          </div>
        </header>

        {/* Figure strip -- ungated summary facts only (on-time is
            scorecard-gated and live in Performance; census C4). */}
        <section className="sd-sec" style={{ marginTop: '22px' }}>
          <div className="sd-figs">
            {supplier.summary && (
              <div className="sd-fig">
                <div className="k">Spend &middot; 12mo</div>
                <div className="v"><Currency value={supplier.summary.spend_12mo}/></div>
                <div className="m">Company-scoped</div>
              </div>
            )}
            {/* QA2-1 (Lane 241): the "Overall grade" card replaces the "Materials"
                catalog-count card (Ryan dislikes it; the count still lives in the
                "Materials supplied" table below). It reuses the SAME aggregated
                worst-of grade the Performance section shows
                (scorecardSummary.grade <- agg.overall_rating), gated on
                scorecardUiEnabled exactly like Performance -- so it is absent when
                the scorecard feature is off. Honest empty ("--" / "Not graded
                yet") when the supplier has no grade; never fabricated.
                QA-3 item 1 (Mega QA 2026-07-22): letter only -- the
                "Outstanding"/"Solid"/etc. word qualifier (gradeColors.js .label)
                is dropped; the letter + its semantic color already carry the
                signal. */}
            {scorecardUiEnabled
              && scorecardSummary
              && scorecardSummary.state !== 'hidden'
              && scorecardSummary.state !== 'loading' && (
              <div className="sd-fig">
                <div className="k">Overall grade</div>
                {scorecardSummary.grade ? (
                  <div className="v" style={{ color: getGradeColor(scorecardSummary.grade).fg }}>
                    {scorecardSummary.grade}
                  </div>
                ) : (
                  <>
                    <div className="v" style={{ color: 'var(--text-3)' }}>Not available</div>
                    <div className="m">Not graded yet</div>
                  </>
                )}
              </div>
            )}
            {supplier.summary.avg_quality_rating ? (
              <div className="sd-fig">
                <div className="k">Quality</div>
                <div className="v" style={{ color: getQualityColor(supplier.summary.avg_quality_rating) }}>
                  {supplier.summary.avg_quality_rating.toFixed(0)}%
                </div>
                <div className="m">{getQualityLabel(supplier.summary.avg_quality_rating)}</div>
              </div>
            ) : null}
            {contractsList.length > 0 && (
              <div className="sd-fig">
                <div className="k">Contracts</div>
                <div className="v">{contractsList.length} <small>active</small></div>
                <div className="m">{soonestDays != null ? `Next expiry in ${soonestDays} days` : 'No upcoming expiry'}</div>
              </div>
            )}
            {/* Delivery coverage remains independent of customer sales. */}
            {deliveryReliabilityEnabled
              && deliverySummary?.slippage?.available
              && deliverySummary.slippage.overall ? (
              <div className="sd-fig">
                <div className="k">On-time delivery</div>
                <div className="v">{Math.round(deliverySummary.slippage.overall.pct_on_time * 100)}%</div>
                <div className="m">{deliverySummary.slippage.overall.coverage_label}</div>
              </div>
            ) : (
              null
            )}
          </div>
        </section>

        {/* ============ SECTION NAV (Lane 226) ============
            The deep sections (Performance / Inventory / Research / Account
            context / Contracts / Notes) are promoted from the old buried
            bottom "Explore" chips to a visible, pinned nav here -- between the
            top cards and the price content -- revealing inline. The
            "everything else -- one calm click away" copy is retired. Every
            section stays reachable (functional parity); the mechanism
            (openExplore / activeExplore) is unchanged. */}
        <section className="sd-sec" style={{ marginTop: '18px' }}>
          <nav className="sd-nav" aria-label="Supplier sections">
            {exploreChips.map(chip => (
              <button
                key={chip.key}
                className={`sd-navitem${activeExplore === chip.key ? ' on' : ''}`}
                onClick={() => openExplore(chip.key)}
                aria-expanded={activeExplore === chip.key}
              >
                {chip.label}
              </button>
            ))}
          </nav>

          {activeExplore && (
            <div className="sd-reveal">
              {activeExplore === 'customers' && <SupplierSalesSection supplierName={supplier.supplier_name} token={token} />}
              {/* --- Research --- */}
              {activeExplore === 'research' && (
                researchLoading ? (
                  <div className="sd-empty"><LoadingSpinner text="Loading research data..." /></div>
                ) : researchData && researchData.research_data && typeof researchData.research_data === 'object' ? (() => {
                  const rd = researchData.research_data;
                  const str = (v) => {
                    if (typeof v === 'string') return v;
                    if (v != null && typeof v === 'object' && !Array.isArray(v)) {
                      const parts = Object.values(v).filter(p => typeof p === 'string' && p.trim());
                      return parts.length > 0 ? parts.join(', ') : '';
                    }
                    return v != null ? String(v) : '';
                  };
                  const certs = rd.certifications ? (Array.isArray(rd.certifications) ? rd.certifications : [rd.certifications]) : [];
                  const industries = rd.industries_served ? (Array.isArray(rd.industries_served) ? rd.industries_served : [rd.industries_served]) : [];
                  const newsStr = str(rd.recent_news).trim();
                  const riskStr = str(rd.risk_factors).trim();
                  const hasNews = newsStr && newsStr !== 'null';
                  const hasRisk = riskStr && riskStr !== 'null';
                  return (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                        <span className="sd-lbl">Company overview</span>
                        {researchData.research_confidence && (
                          <span className={`sd-badge ${researchData.research_confidence === 'high' ? 'good' : researchData.research_confidence === 'medium' ? 'mut' : 'warn'}`}>
                            {researchData.research_confidence} confidence
                          </span>
                        )}
                      </div>
                      {str(rd.description) && <div className="sd-fval" style={{ marginBottom: '16px' }}>{str(rd.description)}</div>}
                      <div className="sd-fieldgrid">
                        {str(rd.headquarters) && <div><div className="sd-flabel">Headquarters</div><div className="sd-fval">{str(rd.headquarters)}</div></div>}
                        {str(rd.ownership) && <div><div className="sd-flabel">Ownership</div><div className="sd-fval">{str(rd.ownership)}</div></div>}
                        {str(rd.products_services) && <div><div className="sd-flabel">Products &amp; Services</div><div className="sd-fval">{str(rd.products_services)}</div></div>}
                        {str(rd.website) && (
                          <div><div className="sd-flabel">Website</div><div className="sd-fval">
                            <a href={str(rd.website).startsWith('http') ? str(rd.website) : `https://${str(rd.website)}`} target="_blank" rel="noopener noreferrer">{str(rd.website)}</a>
                          </div></div>
                        )}
                        {industries.length > 0 && <div><div className="sd-flabel">Industries served</div><div className="sd-fval">{typeof industries[0] === 'string' ? industries.join(', ') : JSON.stringify(industries)}</div></div>}
                        {certs.length > 0 && (
                          <div><div className="sd-flabel">Certifications</div>
                            <div className="sd-chips" style={{ marginTop: '4px' }}>
                              {certs.filter(c => c && typeof c === 'string').slice(0, 10).map((cert, i) => <span key={i} className="sd-blendchip">{cert}</span>)}
                              {certs.length > 10 && <span className="sd-muted" style={{ fontSize: '11px' }}>+{certs.length - 10} more</span>}
                            </div>
                          </div>
                        )}
                      </div>
                      {hasNews && (
                        <div className="sd-callout"><div className="sd-flabel">Recent news</div><div className="sd-fval">{newsStr}</div></div>
                      )}
                      {hasRisk && (
                        <div className="sd-callout warn"><div className="sd-flabel" style={{ color: 'var(--warn)' }}>Risk factors</div><div className="sd-fval" style={{ color: 'var(--warn)' }}>{riskStr}</div></div>
                      )}
                    </div>
                  );
                })() : (
                  <div className="sd-empty">Supplier research has not been completed yet for this supplier.</div>
                )
              )}

              {/* --- Performance (flag-gated) --- */}
              {activeExplore === 'performance' && scorecardUiEnabled && (
                <>
                  <PerformanceTab
                    supplierId={performanceSupplierId}
                    supplierName={supplier.supplier_name}
                    token={token}
                    scorecardUiEnabled={scorecardUiEnabled}
                    resolutionComplete={performanceResolved}
                    /* QA-3 item 5 (Mega QA 2026-07-22): the Wingman-CALCULATED
                       on-time % (pct_on_time, receipt<=promise), passed only
                       when the delivery-reliability flag is on AND real
                       slippage data exists for this supplier -- null
                       otherwise, so KpiCardGrid's subtext falls back to its
                       exact pre-existing text (byte-identical when off). */
                    wingmanOnTimePct={
                      deliveryReliabilityEnabled
                        && deliverySummary?.slippage?.available
                        && deliverySummary.slippage.overall
                        ? deliverySummary.slippage.overall.pct_on_time
                        : null
                    }
                  />
                  {/* Lane 295 reframe: when DELIVERY_RELIABILITY_ENABLED is on,
                      the new Delivery section owns actual lead time (dropping the
                      padded/understated claim), so the Lane-156 padded/understated
                      panel STEPS ASIDE here to avoid showing lead time twice. Both
                      flags OFF on PROD today -> byte-identical. */}
                  {!deliveryReliabilityEnabled && (
                    <LeadtimeReliabilityPanel supplierId={performanceSupplierId} token={token} />
                  )}
                </>
              )}

              {/* --- Delivery reliability (flag-gated, Lane 295) --- */}
              {activeExplore === 'delivery' && deliveryReliabilityEnabled && (
                <DeliveryReliabilityPanel supplierId={deliverySupplierId} token={token} />
              )}

              {/* --- Inventory (flag-gated) --- */}
              {activeExplore === 'inventory' && inventoryUiEnabled && (
                <InventoryTab
                  supplierId={inventorySupplierId}
                  supplierName={supplier.supplier_name}
                  token={token}
                  inventoryUiEnabled={inventoryUiEnabled}
                  resolutionComplete={inventoryResolved}
                />
              )}

              {/* --- Contracts (Lane 198 census C1: wire the modal trigger) --- */}
              {activeExplore === 'contracts' && (
                contractsList.length > 0 ? (
                  <DataTable className="sd-tbl">
                    <thead>
                      <tr>
                        <th>Contract</th>
                        <th className="right">Expiration</th>
                        <th className="right">Contracted Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contractsList.map((c, idx) => {
                        const daysLeft = c.days_until_expiration;
                        const isExpiringSoon = daysLeft != null && daysLeft > 0 && daysLeft <= 90;
                        return (
                          <tr key={c.contract_id || idx} className="click" onClick={() => openContractModal(c)}>
                            <td>
                              <span style={{ fontWeight: 600 }}>{c.contract_number || `Contract #${idx + 1}`}</span>
                              {c.contract_name && <span className="sd-muted" style={{ marginLeft: '8px' }}>{c.contract_name}</span>}
                            </td>
                            <td className="right" style={{ color: isExpiringSoon ? 'var(--warn)' : 'var(--text-2)' }}>
                              {formatDate(c.expiration_date)}{daysLeft != null && daysLeft > 0 ? ` (${daysLeft}d)` : daysLeft != null && daysLeft <= 0 ? ' (expired)' : ''}
                            </td>
                            <td className="num"><Currency value={c.contracted_price} mode="unit-price"/></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </DataTable>
                ) : (
                  <div className="sd-empty">No active contracts for this supplier.</div>
                )
              )}

              {/* --- Team knowledge: customer conversations and supplier notes --- */}
              {activeExplore === 'account_context' && <h3 className="sd-lbl" style={{ marginBottom: '12px' }}>Team knowledge</h3>}
              {activeExplore === 'account_context' && supplierDecisionsEnabled && (
                <SupplierOnRecord
                  supplierName={supplier.supplier_name}
                  data={onRecordState && onRecordState.name === supplierName ? onRecordState.data : null}
                  failed={Boolean(onRecordState && onRecordState.name === supplierName && onRecordState.failed)} />
              )}
              {activeExplore === 'account_context' && (
                interviewIntelLoading ? (
                  <div className="sd-empty"><LoadingSpinner text="Loading team knowledge..." /></div>
                ) : interviewIntel && interviewIntel.entries && interviewIntel.entries.length > 0 ? (
                  <div>
                    <div className="sd-lbl" title="Conversations about customer relationships that mention this supplier." style={{ marginBottom: '10px' }}>Customer conversations</div>
                    {(() => {
                      const byAccount = {};
                      interviewIntel.entries.forEach(e => {
                        const key = e.account_name || 'Unknown';
                        if (!byAccount[key]) byAccount[key] = [];
                        byAccount[key].push(e);
                      });
                      return Object.entries(byAccount).map(([accountName, entries]) => (
                        <div key={accountName} style={{ marginBottom: '16px' }}>
                          <div style={{ paddingBottom: '6px', borderBottom: '1px solid var(--hair)', marginBottom: '8px' }}>
                            <div className="sd-flabel">Customer account</div>
                            <div style={{ fontSize: '13.5px', fontWeight: 600 }}>{accountName}</div>
                          </div>
                          {entries.map((entry, idx) => (
                            <div key={entry.id || idx} style={{ padding: '8px 0', borderBottom: idx < entries.length - 1 ? '1px solid var(--hair)' : 'none' }}>
                              <div className="sd-fval">{entry.content}</div>
                              <div style={{ display: 'flex', gap: '8px', marginTop: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                                {entry.tags && entry.tags.split(',').map(tag => (
                                  <span key={tag.trim()} className="sd-tag" style={{ fontFamily: 'inherit' }}>{tag.trim()}</span>
                                ))}
                                <span className="sd-muted" style={{ fontSize: '11px', marginLeft: 'auto' }}>{entry.created_date}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ));
                    })()}
                  </div>
                ) : (
                  null
                )
              )}

              {/* --- Notes --- */}
              {activeExplore === 'account_context' && (
                <div>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                    <input className="sd-input" type="text" value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddNote()}
                      placeholder="Add a quick note..." style={{ flex: 1 }} />
                    <button className="sd-btn pri" onClick={handleAddNote} disabled={!newNote.trim() || savingNote}>{savingNote ? '...' : 'Add'}</button>
                  </div>
                  {(!supplier.notes || supplier.notes.length === 0) ? (
                    null
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '400px', overflowY: 'auto' }}>
                      {supplier.notes.map((note, idx) => (
                        <div key={note.note_id || idx} className="sd-noterow">
                          <div>{note.text}</div>
                          <div className="meta">{note.created_by || 'System'} {note.created_date ? `· ${note.created_date}` : ''}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {(supplier.relationship_notes || supplier.risk_notes || supplier.strategic_notes) && (
                    <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid var(--hair)' }}>
                      <div className="sd-lbl" style={{ marginBottom: '12px' }}>Context fields</div>
                      {supplier.relationship_notes && <div className="sd-ctxfield"><div className="k">Relationship</div><div className="v">{supplier.relationship_notes}</div></div>}
                      {supplier.risk_notes && <div className="sd-ctxfield"><div className="k">Risk</div><div className="v">{supplier.risk_notes}</div></div>}
                      {supplier.strategic_notes && <div className="sd-ctxfield"><div className="k">Strategic</div><div className="v">{supplier.strategic_notes}</div></div>}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </section>

        {/* ============ PRICE & MARKET (promoted to main scroll) ============ */}
        <section className="sd-sec">
          <div className="sd-sec-h">
            <span className="sd-lbl">Price &amp; market</span>
            <span className="note">three measures, not interchangeable</span>
          </div>

          {/* Lane 134: price summary band -- the four price-number families
              together with the §5 rule-driven notes. Reused verbatim. */}
          <PriceSummaryBand
            priceChangeStatus={priceImpact?.price_change_status || null}
            summarySelected={priceImpact?.summary || null}
            selectedMonths={priceImpactWindow}
            priorWindowGap={priceImpact?.prior_window_gap || null}
            isV2={!!priceImpact?.price_impact_v2}
            blend12={priceBand?.data12?.ppi_blend || null}
            blend24={priceBand?.blend24 || null}
            status12={priceBand?.data12?.price_change_status || null}
            status24={priceBand?.status24 || null}
            ppiConfigured={!!priceBand?.data12?.ppi_blend}
            scorecard={scorecardSummary}
          />

          {/* ---- YoY Price Impact ---- */}
          <div className="sd-panel">
            <div className="sd-panel-h" style={{ alignItems: 'center', marginBottom: priceImpactExpanded ? '10px' : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span
                  onClick={() => setPriceImpactExpanded(v => !v)}
                  style={{ cursor: 'pointer', fontSize: '11px', color: 'var(--text-3)', userSelect: 'none' }}
                >
                  {priceImpactExpanded ? '▼' : '▶'}
                </span>
                <span className="t">YoY Price Impact</span>
                {priceImpact && !priceImpactLoading && (
                  <span className="sd-muted" style={{ fontSize: '12px', marginLeft: '4px' }}>
                    {priceImpact.summary?.total_materials || 0} materials
                  </span>
                )}
              </div>
              <div className="sd-seg">
                {[6, 12, 24].map(m => (
                  <button key={m} className={priceImpactMonths === m ? 'on' : ''} onClick={() => handlePriceImpactMonthsChange(m)}>{m}mo</button>
                ))}
              </div>
            </div>

            {priceImpactExpanded && (
              <>
                {yoyInsufficientMessage && (
                  <div className="sd-sub" role="note" style={{ marginBottom: '10px', color: 'var(--text-2)' }}>
                    {yoyInsufficientMessage}
                  </div>
                )}
                {priceImpactLoading ? (
                  <div className="sd-empty">Loading price impact data...</div>
                ) : !priceImpact || !priceImpact.materials?.length ? (
                  <div className="sd-empty">
                    {priceImpact?.data_availability ? (
                      <div>
                        <p style={{ marginBottom: '6px' }}>{priceImpact.data_availability.message}</p>
                        <p style={{ fontSize: '12px' }}>
                          PO history: {priceImpact.data_availability.earliest_po} to {priceImpact.data_availability.latest_po}
                        </p>
                      </div>
                    ) : 'No price impact data for this period.'}
                  </div>
                ) : (
                  <>
                    {/* Sub-headline */}
                    <div className="sd-sub" style={{ marginBottom: '10px' }}>
                      {priceImpact.summary.materials_increased} increased
                      {' · '}{priceImpact.summary.materials_decreased} decreased
                      {priceImpact.summary.materials_new > 0 && ` · ${priceImpact.summary.materials_new} new`}
                      {priceImpact.summary.materials_discontinued > 0 && ` · ${priceImpact.summary.materials_discontinued} discontinued`}
                      {/* Round 4 (D3): the net-impact headline is withheld when
                          the canonical status says the headline is insufficient,
                          even if legacy figures survived server-side. */}
                      {priceImpact.price_impact_v2 && !yoyInsufficientMessage && priceImpact.summary.total_dollar_impact != null && (
                        <span style={{ marginLeft: '12px', fontWeight: 600, color: priceImpact.summary.total_dollar_impact > 0 ? 'var(--up)' : priceImpact.summary.total_dollar_impact < 0 ? 'var(--down)' : 'var(--text-3)' }}>
                          Net impact {priceImpact.summary.total_dollar_impact >= 0 ? '+' : '-'}{formatCurrency(Math.abs(priceImpact.summary.total_dollar_impact))}
                          {typeof priceImpact.summary.materials_compared === 'number'
                            && Math.round(Math.abs(priceImpact.summary.total_dollar_impact)) !== 0
                            ? ` across ${pluralize(priceImpact.summary.materials_compared, 'comparable material')}`
                            : ''}
                        </span>
                      )}
                      {/* Lane 1414: the same one-line flag as the band, beside the
                          net impact it qualifies. Null with the switch off. */}
                      {priceImpact.price_impact_v2 && !yoyInsufficientMessage && priceImpact.summary.total_dollar_impact != null
                        && priceChangeLowCoverageLine(priceImpact.price_change_status) && (
                        <span style={{ marginLeft: '12px', color: 'var(--warn)' }}>
                          {priceChangeLowCoverageLine(priceImpact.price_change_status)}
                        </span>
                      )}
                      {priceImpact.period?.current && (
                        <span style={{ marginLeft: '12px', fontStyle: 'italic' }}>
                          ({priceImpact.period.current} vs {priceImpact.period.prior})
                        </span>
                      )}
                    </div>

                    {/* Lane 138: history-gap disclosure under the window label. */}
                    {(priceImpact.summary?.materials_compared || 0) > 0
                      && formatPriorWindowGap(priceImpact.prior_window_gap) && (
                      <div data-testid="yoy-prior-window-gap" className="sd-note warn" style={{ marginTop: 0, marginBottom: '10px' }}>
                        {formatPriorWindowGap(priceImpact.prior_window_gap)}
                      </div>
                    )}

                    {/* Lane 226: the grain bridge -- the same purchased<->comparable
                        bridge the deck carries, so the net-impact basis is never
                        mistaken for the full catalog. Each count is a labelled fact
                        (no false arithmetic between catalog and analysed); the clause
                        mirrors the deck's definition, window-aware. Gated on a
                        comparison existing, like the deck. */}
                    {(priceImpact.summary?.materials_compared || 0) > 0 && (() => {
                      const w = priceImpactWindow || priceImpactMonths;
                      const catalog = supplier.summary?.material_count || 0;
                      const inCmp = priceImpact.summary?.total_materials || 0;
                      const cmp = priceImpact.summary?.materials_compared || 0;
                      return (
                        <div className="sd-grain" data-testid="yoy-grain-bridge">
                          {catalog > 0 && <><b>{catalog.toLocaleString()}</b> in the catalog{' · '}</>}
                          <b>{inCmp.toLocaleString()}</b> in this {w}-month comparison
                          {' · '}<b>{cmp.toLocaleString()}</b> comparable.{' '}
                          &ldquo;Comparable&rdquo; counts only materials purchased in both the current and
                          prior {w} months, a subset. The rows below are this table&rsquo;s own
                          per-part detail; they are not the basis of the headline figure.
                        </div>
                      );
                    })()}

                    {/* Materials table -- top 20 */}
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      <DataTable className="sd-tbl">
                        <thead>
                          <tr>
                            <th>Material</th>
                            <th className="right">Prior Avg</th>
                            <th className="right">Current Avg</th>
                            <th className="right"><MetricInfo metricKey="supplier_price_impact_yoy">Change %</MetricInfo></th>
                            <th className="right">Volume</th>
                            {priceImpact.price_impact_v2 && (
                              <th
                                className={`right sortable${yoyImpactSortDir ? ' on' : ''}`}
                                onClick={toggleYoyImpactSort}
                                title="Sort by impact"
                              >
                                Impact{yoyImpactSortDir && <span className="sd-arrow">{yoyImpactSortDir === 'asc' ? '▲' : '▼'}</span>}
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {yoyRows.map((mat, idx) => {
                            const isIncrease = (mat.change_pct || 0) > 0;
                            const changeColor = mat.status === 'both' ? (isIncrease ? 'var(--up)' : 'var(--down)') : 'var(--text-3)';
                            return (
                              <tr key={idx} className="click" onClick={() => navigate(`/materials/${mat.material_number}`)}>
                                <td>
                                  <div className="sd-mname" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <div>
                                      <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--accent)' }}>{mat.material_number}</div>
                                      <div className="mt" style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mat.material_name}</div>
                                    </div>
                                    {mat.status === 'new' && <span className="sd-pill pri">NEW</span>}
                                    {mat.status === 'discontinued' && <span className="sd-pill mut">DISC</span>}
                                  </div>
                                </td>
                                <td className="num">{mat.prior_avg_price != null ? `$${mat.prior_avg_price.toFixed(2)}` : 'Not available'}</td>
                                <td className="num">{mat.current_avg_price != null ? `$${mat.current_avg_price.toFixed(2)}` : 'Not available'}</td>
                                <td className="num" style={{ fontWeight: 600, color: changeColor }}>
                                  {mat.change_pct != null ? `${isIncrease ? '+' : ''}${(mat.change_pct * 100).toFixed(1)}%` : 'Not available'}
                                </td>
                                <td className="right">{mat.current_quantity?.toLocaleString() || 'Not available'}</td>
                                {priceImpact.price_impact_v2 && (
                                  <td className="num" style={{ fontWeight: 600, color: mat.status === 'both' ? ((mat.dollar_impact || 0) > 0 ? 'var(--up)' : (mat.dollar_impact || 0) < 0 ? 'var(--down)' : 'var(--text-3)') : 'var(--text-3)' }}>
                                    {mat.status === 'both'
                                      ? `${(mat.dollar_impact || 0) >= 0 ? '+' : '-'}${formatCurrency(Math.abs(mat.dollar_impact || 0))}`
                                      : 'Not available'}
                                    {mat.is_outlier && <span title="Unusual mover - verify" className="sd-out">*</span>}
                                    <AnomalyExclusionTag show={mat.had_anomaly_exclusion} count={mat.anomaly_excluded_count} exclusions={mat.anomaly_exclusions} />
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </DataTable>
                    </div>
                    {priceImpact.materials.length > 20 && (
                      <div className="sd-note" style={{ textAlign: 'center' }}>
                        Showing top 20 of {priceImpact.materials.length} materials by impact
                      </div>
                    )}
                  </>
                )}

                {/* Lane 135: on-page legend for the row markers actually shown.
                    Lane 226: reads the sorted-and-sliced rows so it reflects
                    exactly what the table displays. */}
                {priceImpact?.price_impact_v2 && priceImpact.materials?.length > 0 && (
                  <MarkerLegend kinds={[
                    ...(yoyRows.some(m => m.is_outlier) ? ['outlier'] : []),
                    ...(yoyRows.some(m => m.had_anomaly_exclusion) ? ['exclusion'] : []),
                  ]} />
                )}

                {/* Lane 96: lot-priced dropped rows, named, outside the table. */}
                {priceImpact?.dropped_rows?.length > 0 && (
                  <div className="sd-note" style={{ borderTop: '1px solid var(--hair)', paddingTop: '8px' }}>
                    <span style={{ fontWeight: 600 }}>Not shown - lot-priced (unit price not meaningful): </span>
                    {priceImpact.dropped_rows.map(d => d.material_name || d.material_number).join(', ')}
                  </div>
                )}
              </>
            )}
          </div>

          {/* ⛔ THE SEPARATE "Price change" PANEL WAS REMOVED (Lane 1021).
              It added a THIRD price figure to a page that already had two that
              disagreed. Ryan, on seeing it: "We have what a price change cost
              you, like, in two places now... What are we doing?"

              The one number now REPLACES the old figures at the source — the
              server overwrites `summary.*` and `ppi_blend.your_weighted_price_
              change_pct`, so the Price Summary band, the YoY panel and the
              Price vs Market card below all render it with no separate panel
              and no second opinion. See helpers/price_change_dossier. */}
          {/* ---- Price vs Market (PPI Blend) ---- */}
          {priceImpact && !priceImpact.ppi_blend && (
            <div className="sd-panel">
              <div className="sd-panel-h"><span className="t">Price vs Market</span></div>
              <div className="sd-sub" style={{ padding: '8px 0' }}>
                PPI benchmark profile not yet configured for this supplier. An admin can set this up from the company detail page.
              </div>
            </div>
          )}
          {priceImpact?.ppi_blend && (
            <div className="sd-panel">
              <div className="sd-panel-h" style={{ marginBottom: '12px' }}>
                <span className="t">
                  <MetricInfo metricKey="supplier_ppi_gap" values={{ coverage_pct: priceImpact.ppi_blend?.spend_coverage_pct }}>
                    Price vs Market
                  </MetricInfo>
                </span>
              </div>

              {/* Lane 718 (Ryan's D52.2, register §9.2): THE HONEST EMPTY STATE
                  for the supplier this engine can never speak for.

                  Ryan: "For suppliers where there is not enough data for PPI,
                  there should be a banner in that section on their dossier."

                  ⚠️ IT IS A SIBLING OF THE "not yet configured" PANEL ABOVE, NOT
                  A REPLACEMENT FOR IT. Those are two silences with OPPOSITE
                  remedies: a missing profile is fixable by mapping the supplier
                  to an index, and this one is not fixable at all -- the
                  benchmark IS mapped and the purchase orders underneath it
                  cannot support a price change. They are kept apart
                  STRUCTURALLY: the branch at the top of this section owns
                  `ppi_blend === null` and renders instead of this whole panel,
                  so this banner is unreachable in that state. Do not merge the
                  two messages.

                  Panel-level and ABOVE the tiles deliberately: the three tiles
                  each say "Insufficient purchase history for this window",
                  which a reader meets three times and can still read as "try
                  another window". The banner is the one statement that the
                  window is not the problem. The per-tile line stays for the
                  PARTIAL case (a figure at 12 but not 24), which the banner
                  refuses to fire on. */}
              <PpiInsufficientBanner blends={[6, 12, 24].map(ppiBlendForWindow)} />

              {/* ⛔ Lane 950 / D53 -- THE WELD. This block and the period tiles
                  below it are ONE unit: the tiles are the only place this panel
                  prints your-change / market / gap, and this sits immediately
                  above them so the caveat is read BEFORE the figures rather than
                  discovered under them. Panel-level, not per-tile, because
                  geography is a fact about the SUPPLIER -- `_benchmark_geo`'s
                  query carries no date predicate, so the verdict is identical at
                  6, 12 and 24 (Lane 944 measured that) and a per-tile copy would
                  say the same thing three times inside one card row.

                  The lead-in is the same "Benchmark geography" lead the server's
                  `geo_caveat` uses, so the dossier and chat open the disclosure
                  with identical words. Everything after it is the server's string,
                  verbatim -- ⚠️ COPY AWAITING RYAN'S SIGNATURE, do not reword it
                  here. `sd-grain` is the existing quiet-inset class the sibling
                  `PpiInsufficientBanner` uses; no new CSS. */}
              {ppiGeoCaveat && (
                <div className="sd-grain" data-testid="ppi-geo-caveat">
                  <b>Benchmark geography:</b> {ppiGeoCaveat}
                </div>
              )}

              {/* Period cards -- each clickable = the window toggle */}
              <div className="sd-measures">
                {[6, 12, 24].map(period => {
                  const blend = ppiBlendForWindow(period);
                  // Lane 1414: both null with the switch off (today's tile).
                  const status = ppiStatusForWindow(period);
                  const absence = priceChangeAbsence(status);
                  const lowCoverage = priceChangeLowCoverageLine(status);
                  const isActive = period === priceImpactMonths;
                  const isPrimary = period === 12;
                  const fmtVal = (v) => v != null ? `${v >= 0 ? '+' : ''}${v}%` : '--';
                  // Lane 382 item 3: the local `fmtForwardDollars` is RETIRED --
                  // the shared ForwardRunRateLine owns the wording, the dollar
                  // format (utils/priceSectionNotes fmtSignedDollars, which this
                  // local copy duplicated) and the colour.
                  const gapColor = (g) => g != null ? (g > 0 ? 'var(--warn)' : g < 0 ? 'var(--good)' : 'var(--text)') : 'var(--text-3)';
                  return (
                    <div
                      key={period}
                      className={`sd-measure${isActive ? ' on' : ''}${isPrimary ? ' primary' : ''}`}
                      onClick={() => handlePriceImpactMonthsChange(period)}
                    >
                      <div className="mk" style={isActive ? { color: 'var(--accent)' } : undefined}>{period}mo</div>
                      {blend ? (
                        // Lane 1414: the blend's 'insufficient' is the old engine's
                        // verdict. When the server's switch is on and the one price
                        // change was measured, the figure shows (market "--").
                        // A typed absence (switch on) wins over it too (Codex code review #5).
                        blend.confidence === 'insufficient' && !absence
                          && !(status && status.low_coverage_enabled === true && status.status === 'applied') ? (
                          <div className="sd-sub" style={{ padding: '8px 0' }}>Insufficient purchase history for this window</div>
                        ) : absence ? (
                          // Lane 1414: no buyer price change, so no gap and no
                          // forward dollar. Never the retired figure.
                          <>
                            <div className="row"><span className="lab">Market</span><span className="num">{fmtVal(blend.weighted_ppi_change_pct)}</span></div>
                            <div className="sd-sub" style={{ padding: '4px 0' }}>{absence}</div>
                          </>
                        ) : (
                          <>
                            <div className="row"><span className="lab">Your Price Change</span><span className="num">{fmtVal(blend.your_weighted_price_change_pct)}</span></div>
                            <div className="row"><span className="lab">Market</span><span className="num">{fmtVal(blend.weighted_ppi_change_pct)}</span></div>
                            <div className="row gap"><span className="lab" style={{ fontWeight: 600 }}>Gap</span><span className="num" style={{ color: gapColor(blend.gap_pct) }}>{fmtVal(blend.gap_pct)}</span></div>
                            {/* P-3 (Lane 357): the point-to-point %'s PAIRED dollar
                                (round-4 execution plan §0, pairing rules P3/P6) --
                                what that percentage means in money if today's prices
                                hold. Reads `forward_run_rate_surface_usd`, which the
                                engine emits ONLY for the negotiation case (sign-agreeing
                                AND a cost increase), so a sign-disagreeing dollar and the
                                both-negative "we'll save money next year" projection are
                                structurally unrenderable here. Absent key (every legacy
                                basis) renders nothing -- the flag-OFF card is byte-identical.

                                Lane 382 item 3: rendered through the SHARED
                                ForwardRunRateLine -- the SAME component the price
                                summary band's trend tiles use. Ryan: "if it's saying
                                the same thing, use the same font ... use the same
                                terminology on the price-vs-market as on the price
                                summary, because it's the same thing." The dollar was
                                tinted var(--warn) (amber) here and var(--up) (red) on
                                the band; the component owns var(--up) for both, so the
                                two surfaces can never diverge in wording OR colour. */}
                            <ForwardRunRateLine
                              usd={blend.forward_run_rate_surface_usd}
                              months={period}
                              className="conf sd-muted"
                              testId={`ppi-forward-${period}`}
                            />
                            {/* Lane 1414: the low-coverage flag REPLACES the two coverage
                                lines below, so this tile never prints two different
                                coverage percentages (the D71 confusion). */}
                            {lowCoverage && <div className="conf" style={{ color: 'var(--warn)' }}>{lowCoverage}</div>}
                            {!lowCoverage && blend.confidence === 'low' && <div className="conf" style={{ color: 'var(--warn)' }}>Limited data: {blend.spend_coverage_pct}% of spend</div>}
                            {/* ✅ D71 (Ryan, 2026-08-09), Option 1 of three: NAME THE MEASURE,
                                keep both numbers. This page prints TWO coverage percentages a
                                few centimetres apart, inside a section whose own header says
                                "three measures -- not interchangeable", and until today both
                                read as the same claim. They are not: this one is the share of
                                spend on materials with a MARKET INDEX attached (the
                                `_compute_ppi_blend` denominator); the price band's is the share
                                of spend with a comparable PRIOR PURCHASE (`weighted_yoy_rows`).
                                ⛔ THE NUMBER DOES NOT MOVE -- Option 3 (one engine, one figure)
                                is the Rule 120 answer and was explicitly NOT taken, because
                                `spend_coverage_pct` drives the confidence tier that nine
                                consumers gate on, including which deck pages are admissible.
                                Only the words in front of it changed. */}
                            {!lowCoverage && blend.confidence === 'medium' && blend.market_basis !== 'part_index' && <div className="conf sd-muted">Market comparison covers {blend.spend_coverage_pct}% of your spend with them</div>}
                            {/* Lane 1415: with each part on its own index, the market
                                figure covers the compared parts that have one. Said only
                                when some do not (the share of the compared basket). */}
                            {partIndexCoverageLine(blend) && <div className="conf sd-muted">{partIndexCoverageLine(blend)}</div>}
                          </>
                        )
                      ) : (
                        <div className="sd-sub" style={{ padding: '4px 0' }}>Loading...</div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Lane 713 (Ryan's D50.4, and his Tier-3 override on placement):
                  THE DOSSIER'S ONE FORWARD NUMBER, on Ryan's ladder formula --
                  the same `deck_forward.forward_exposure_facts()` fact set page
                  7, slide 10 and the chat use.

                  PANEL-LEVEL, BENEATH THE TILES, DELIBERATELY. The three tiles
                  above are PAIRING tiles: the PPI forward key existed there only
                  when its sign agreed with that tile's own "Your Price Change"
                  (pairing rules P3/P6 -- the % and the $ are one statement).
                  This figure is signed and independently derived, so inside a
                  tile it would sit a "prices rose +1.02%" over a "-$793 saving"
                  on Amorim Cork, and a "-3.11%" over "+$24,855" on Eriks BV --
                  5 of the 17 affected suppliers. Out here it is the panel's own
                  forward statement, subordinate to no single window's
                  percentage. Do not move it into the loop above.

                  The competing PPI-basis line inside those tiles is closed
                  SERVER-side (the endpoint pops `forward_run_rate_surface_usd`
                  at every window when the flag is on), so this cannot render
                  beside it -- one forward figure on this page, or none. */}
              <ForwardExposureLine facts={forwardExposure} />

              {/* Lane 96: guard-exclusion note. */}
              {(priceImpact.ppi_blend.anomalous_pos_excluded > 0 || priceImpact.ppi_blend.lot_priced_materials_excluded > 0) && (
                <div className="sd-note" style={{ marginTop: '12px' }}>
                  Excludes {[
                    priceImpact.ppi_blend.anomalous_pos_excluded > 0 ? `${pluralize(priceImpact.ppi_blend.anomalous_pos_excluded, 'anomalous order')}` : null,
                    priceImpact.ppi_blend.lot_priced_materials_excluded > 0 ? `${pluralize(priceImpact.ppi_blend.lot_priced_materials_excluded, 'lot-priced material')}` : null,
                  ].filter(Boolean).join(' and ')} from "Your Price Change". Verify source POs.
                </div>
              )}

              {/* Lane 31 Stage 5 / Lane 149: §5.FO honesty caveat. */}
              {/* P-3 (Lane 357): the §5.FO caveat follows the ENGINE's own basis
                  discriminator, never a flag. Legacy string is byte-identical when
                  `price_basis` is absent.

                  LANE 390 — the point-to-point BASIS EXPOSITION is GONE from the
                  point-to-point branch. Ryan (staging QA 2026-07-25): "On the price
                  versus market, get rid of this description about your price changes
                  point to point. It's too much in there. Take that part out."

                  Two sentences left, both exposition rather than data caveat:

                    (1) `"Your Price Change" is point-to-point — their current price
                        level against the level at the start of the window.`
                        This is the basis description Ryan named, and since Lane 382
                        it is ALSO in the card header's MetricInfo hover
                        (`supplier_ppi_gap`) and on the band tiles' hovers — the card
                        was saying it a second time in prose.

                    (2) `Its dollar companion is the forward projection shown with it;
                        the YoY panel above is a separate, historical figure…`
                        Its subject pronoun is (1), so it cannot survive (1)'s removal
                        without a rewrite, and it restates two labels the surfaces
                        already carry themselves: the forward line is self-labelled
                        `(projection)` (ForwardRunRateLine, Lane 382 item 3) and the
                        YoY panel is a separate, separately-titled panel.

                  What the pairing rules actually require here is the ABSENCE of the
                  legacy "the dollar cost is in the YoY panel above" sentence on this
                  basis — that negative is unchanged and still test-pinned. Removing a
                  positive restatement of it does not reintroduce the mixing.

                  KEPT: the §5.FO market-publish-lag caveat. It is the one genuine data
                  qualifier in the block — it says the gap is measured against a market
                  that stops ~2 months short. Nothing else on the card or in any hover
                  says that, so it is not duplication.

                  LANE 426 — the lag's DIRECTION was stated BACKWARDS here and in the
                  rendered string below (and in context.py, so the assistant repeated
                  it). Both said the lag "makes the gap a conservative read". It does
                  the opposite, and the arithmetic is not subtle:

                    gap = your − market  (analytics.py:3080 point-to-point;
                                          :3735 / :3748 legacy — every construction)

                  The market sits in the SUBTRAHEND, so a market side that reads low
                  makes the difference LARGER, not smaller. your +15.0% against a true
                  market +6.0% is a +9.0pp gap; against a lag-shortened +5.0% it is
                  +10.0pp. Per Rule 115 (context.py:929) a positive gap renders as
                  "BUYER LEVERAGE — quantify the gap as the ask", so the displayed gap
                  IS the negotiating ask: the old copy told the buyer to lean on a
                  figure while labelling its bias in the wrong direction.

                  The bias runs the SAME way when the supplier is BELOW market: your
                  +2.0% vs true market +6.0% is −4.0pp, vs a shown +5.0% it is −3.0pp
                  — still shifted upward, understating the supplier's catch-up case
                  (context.py:937). So there is no regime in which the lag makes this
                  gap conservative; it always biases toward the above-market reading.
                  Do NOT reintroduce "conservative" here — it is pinned against.

                  The LEGACY branch is untouched (that is what PROD renders until the
                  round-4 PPI flags flip; Ryan named the point-to-point copy, and it is
                  the branch he is looking at on staging). Trimming it too is a filed
                  one-line follow-up, not an unrequested change to a live string. */}
              {priceImpact.supplier_engine_unified && (
                <div className="sd-note" style={{ marginTop: '12px', paddingBottom: '12px', borderBottom: '1px solid var(--hair)' }}>
                  {priceImpact.ppi_blend?.price_basis === 'point_to_point' ? (
                    <>
                      The market side ends about two months before today (market data publishes on
                      a lag), which slightly overstates the gap rather than understating it.
                    </>
                  ) : (
                    <>
                      "Your Price Change" measures movement inside the window shown. A price step from
                      before it won't appear here; the dollar cost is in the YoY panel above. The window
                      ends about two months before today (market data publishes on a lag).
                    </>
                  )}
                </div>
              )}

              {/* Chart toggle + trend */}
              {priceImpact.ppi_data_points?.length >= 2 && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '14px 0 10px' }}>
                    <div className="sd-sub">{priceImpactMonths}-month trend, normalized to % change</div>
                    <div className="sd-seg">
                      {['blended', 'category'].map(mode => (
                        <button key={mode} className={ppiChartMode === mode ? 'on' : ''} onClick={() => setPpiChartMode(mode)}>
                          {mode === 'blended' ? 'Blended' : 'By Category'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Blended view chart (recharts content untouched -- Ryan carve-out) */}
                  {ppiChartMode === 'blended' && (() => {
                    const pts = priceImpact.ppi_data_points || [];
                    const cleanPts = pts.filter(p => !p.is_outlier);
                    // Lane 1414: with no buyer price change for this window the
                    // server leaves the old line in the points, so it is not
                    // drawn (nor scaled for). Always true with the switch off.
                    const showYours = !priceChangeAbsence(priceImpact.price_change_status);
                    const allVals = cleanPts.flatMap(p => (showYours ? [p.your_price_index, p.blended_ppi_index] : [p.blended_ppi_index])).filter(v => v != null);
                    const rawMin = allVals.length ? Math.min(...allVals) : -5;
                    const rawMax = allVals.length ? Math.max(...allVals) : 5;
                    // QA-3 item 6 (Mega QA 2026-07-22): the domain always spans
                    // zero so the y=0 ReferenceLine below is never clipped off
                    // the visible axis -- e.g. when every point is negative
                    // (say -13..-11), the old padding-only math could land a
                    // domain like [-16,-9] with zero nowhere on screen, making
                    // "float around zero" invisible. Data/series untouched.
                    const yMin = Math.min(0, rawMin);
                    const yMax = Math.max(0, rawMax);
                    const pad = (yMax - yMin) * 0.15 || 5;
                    return (
                      <>
                        <ResponsiveContainer width="100%" height={240}>
                          <LineChart data={chartTimeData(pts)} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                            <CartesianGrid {...chartGrid} />
                            <XAxis {...chartAxis} {...chartTimeAxis} ticks={chartTicks(pts)} tickFormatter={chartMonth} />
                            <YAxis {...chartAxis} domain={[Math.floor(yMin - pad), Math.ceil(yMax + pad)]} tickFormatter={v => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`} width={55} />
                            <Tooltip content={({ active, payload, label }) => {
                              if (!active || !payload?.length) return null;
                              const d = new Date(typeof label === 'number' ? label : toLocalEpoch(label));
                              const point = payload[0]?.payload;
                              return (
                                <div style={chartTooltipStyle}>
                                  <div style={{ fontWeight: 600, marginBottom: '6px', color: '#111827' }}>{d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</div>
                                  {payload.map((entry, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: entry.color, display: 'inline-block' }} />
                                      <span style={{ color: '#6B7280' }}>{entry.name}:</span>
                                      <span style={{ fontWeight: 500, fontFamily: 'monospace' }}>{entry.value != null ? `${entry.value >= 0 ? '+' : ''}${entry.value.toFixed(1)}%` : 'N/A'}</span>
                                    </div>
                                  ))}
                                  {showYours && point?.is_outlier && (
                                    <div style={{ marginTop: '4px', fontSize: '11px', color: '#D97706', fontStyle: 'italic' }}>
                                      Actual: {point.your_price_index >= 0 ? '+' : ''}{point.your_price_index.toFixed(1)}% (capped for scale)
                                    </div>
                                  )}
                                </div>
                              );
                            }} />
                            <Legend wrapperStyle={{ fontSize: '11px' }} />
                            <ReferenceLine y={0} stroke="#9CA3AF" strokeDasharray="3 3" strokeWidth={1} />
                            {showYours && (
                            <Line type="monotone" dataKey="display_value" name="Your Price Change" stroke={chartPalette[0]} strokeWidth={2} dot={(props) => {
                              const point = pts[props.index];
                              if (point?.is_outlier) {
                                return <circle key={props.index} cx={props.cx} cy={props.cy} r={4} fill="none" stroke="#D97706" strokeWidth={2} />;
                              }
                              return <circle key={props.index} cx={props.cx} cy={props.cy} r={3} fill={chartPalette[0]} />;
                            }} connectNulls />
                            )}
                            <Line type="monotone" dataKey="blended_ppi_index" name="Blended PPI" stroke={chartPalette[6]} strokeWidth={2} strokeDasharray="6 3" dot={false} connectNulls />
                          </LineChart>
                        </ResponsiveContainer>
                        {/* Outlier footnote */}
                        {showYours && (priceImpact.outlier_count || 0) > 0 && (
                          <div style={{ marginTop: '8px' }}>
                            <div onClick={() => setOutlierFootnoteOpen(!outlierFootnoteOpen)} style={{ fontSize: '12px', color: 'var(--text-3)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ fontSize: '10px' }}>{outlierFootnoteOpen ? '▼' : '▶'}</span>
                              {priceImpact.outlier_count} data point{priceImpact.outlier_count !== 1 ? 's' : ''} excluded from scale
                            </div>
                            {outlierFootnoteOpen && priceImpact.outliers?.map((o, i) => (
                              <div key={i} style={{ fontSize: '12px', color: 'var(--text-3)', marginLeft: '14px', marginTop: '2px', fontFamily: 'var(--mono)' }}>
                                {new Date(toLocalEpoch(o.month)).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}: {o.original_value >= 0 ? '+' : ''}{o.original_value.toFixed(1)}%
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Data quality warning */}
                        {(priceImpact.outlier_count || 0) >= 3 && (
                          <div className="sd-note warn" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                              <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                            </svg>
                            This supplier has {priceImpact.outlier_count}+ months with unusual price data. Consider reviewing PO records for UOM or data entry issues.
                          </div>
                        )}
                      </>
                    );
                  })()}

                  {/* P-3 (Lane 357) — CHART DEFECT 1: the blank "By Category" view.
                      The enclosing block gates on ppi_data_points.length >= 2, but this
                      branch gated on a NON-EMPTY breakdown with no else, so whenever the
                      chart renders and `ppi_by_category` is empty the tab drew a visible
                      toggle above empty space — no chart, no message. `ppiChartMode` is
                      component state that a window switch does not reset, so the
                      deterministic repro is: open By Category at a window that has a
                      breakdown, then switch windows. Measured on real data at 6/12/24mo
                      under both bases (see the banked preview). The honest empty state
                      below replaces the silence; the toggle deliberately STAYS so the
                      control does not vanish mid-session. */}
                  {ppiChartMode === 'category' && !(priceImpact.ppi_by_category?.length > 0) && (
                    <div className="sd-sub" style={{ padding: '8px 0' }} data-testid="ppi-category-empty">
                      No per-category breakdown is available for this window. The blended view above still applies.
                    </div>
                  )}
                  {/* Category breakdown view */}
                  {ppiChartMode === 'category' && priceImpact.ppi_by_category?.length > 0 && (
                    <div>
                      {priceImpact.ppi_like_for_like && (
                        <div className="sd-sub" style={{ marginBottom: '10px' }}>
                          Per-series figures use the full window; the headline Market and Gap are measured per material's own window, so they won't match exactly.
                        </div>
                      )}
                      {/* Lane 1414 (Codex code review #1): with the server's switch on,
                          the per-series "you" and gap come from the retired engine
                          and can contradict the card. The one price change is measured
                          across all parts, so each row shows the market only. */}
                      {categoryMarketOnly && (
                        <div className="sd-sub" style={{ marginBottom: '10px' }}>
                          Your price change is measured across all parts, so it is not split by category.
                        </div>
                      )}
                      {priceImpact.ppi_by_category.map((cat) => {
                        const gap = cat.gap_pct;
                        const barW = Math.min(50, Math.abs(gap) * 3);
                        if (categoryMarketOnly) {
                          return (
                            <div key={cat.series_id} className="sd-crow">
                              <div>
                                <div className="cn">{cat.series_name}</div>
                                <div className="cmeta">
                                  market {cat.ppi_change_pct >= 0 ? '+' : ''}{cat.ppi_change_pct}%{' · '}{cat.spend_weight_pct}% of spend
                                </div>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div key={cat.series_id} className="sd-crow">
                            <div>
                              <div className="cn">{cat.series_name}</div>
                              {/* P-3 (Lane 357) — CHART DEFECT 2, LIVE ON PROD.
                                  This row rendered "{cat.material_count} materials"
                                  against a key the backend has NEVER emitted, so every
                                  By-Category row on PROD reads literally "undefined
                                  materials (85.0% of spend)". `_compute_ppi_blend`
                                  builds these entries with exactly six keys
                                  (series_name, series_id, spend_weight_pct,
                                  ppi_change_pct, your_price_change_pct, gap_pct) on
                                  BOTH bases — a supplier's PPI profile maps SERIES to
                                  WEIGHTS, and there is no material→series mapping on
                                  this path, so a per-series material count does not
                                  exist to be shown. The clause is therefore REMOVED
                                  rather than back-filled: substituting the
                                  supplier-level `materials_included` into a per-series
                                  row would be a fabricated count (Rule 116). The spend
                                  weight, which the entry genuinely carries, stays.
                                  Basis-independent — correct at both flag states. */}
                              <div className="cmeta">
                                you {cat.your_price_change_pct >= 0 ? '+' : ''}{cat.your_price_change_pct}% &middot; market {cat.ppi_change_pct >= 0 ? '+' : ''}{cat.ppi_change_pct}%
                                {' · '}{cat.spend_weight_pct}% of spend
                              </div>
                            </div>
                            <div className="cbar"><i className={gap < 0 ? 'neg' : ''} style={{ width: `${barW}%` }} /></div>
                            <div className={`cg ${gap > 0 ? 'pos' : gap < 0 ? 'neg' : ''}`}>{gap >= 0 ? '+' : ''}{gap}%</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {/* Lane 1428 (Ryan, 2026-10-25): on the part-index basis the chip
                  row with series codes is replaced by ONE line, "Blended market
                  index, composed of X, Y, Z" (top three, short names), with every
                  index and its share on hover. The line and the details are the
                  server's `market_composition`, built from the same
                  `series_breakdown` whose shares are the Market figure's weights.
                  The whole box reads `ppiBlendForWindow(priceImpactMonths)`, the
                  object the highlighted tile's Market figure comes from, so the
                  line follows the selected window and never mixes two responses
                  (consult #1 risk 2). Legacy basis (no `market_composition`):
                  the box below renders exactly as before, because its weights are
                  the profile's, not spend shares. */}
              {(() => {
                const mixBlend = ppiBlendForWindow(priceImpactMonths);
                if (!mixBlend?.market_composition) return null;
                return (
                  <div style={{ marginTop: '16px', padding: '12px 14px', borderRadius: '8px', background: 'var(--panel-2)', border: '1px solid var(--border)' }}>
                    <MarketBlendLabel
                      composition={mixBlend.market_composition}
                      months={priceImpactMonths}
                      provenance={mixBlend.ppi_provenance}
                    />
                    <div className="sd-note" style={{ marginTop: '6px' }}>
                      {mixBlend.materials_included != null
                        ? `${mixBlend.materials_included} of ${mixBlend.materials_total} materials included in analysis (${mixBlend.spend_coverage_pct}% of spend)${mixBlend.materials_excluded > 0 ? `. ${mixBlend.materials_excluded} excluded due to insufficient purchase history.` : ''}`
                        : `${mixBlend.materials_mapped} of ${mixBlend.materials_total} materials mapped to PPI indices`}
                    </div>
                  </div>
                );
              })()}

              {/* Blend composition (legacy basis) */}
              {!ppiBlendForWindow(priceImpactMonths)?.market_composition
                && priceImpact.ppi_blend.market_basis !== 'part_index'
                && priceImpact.ppi_blend.series_breakdown?.length > 0 && (
                <div style={{ marginTop: '16px', padding: '12px 14px', borderRadius: '8px', background: 'var(--panel-2)', border: '1px solid var(--border)' }}>
                  <div className="sd-lbl" style={{ marginBottom: '8px' }}>PPI Blend Composition</div>
                  <div className="sd-chips">
                    {/* Lane 814: the series ID rides beside the name when
                        provenance is on. `series_id` was already bound here as
                        the React key and thrown away -- a buyer asking "which
                        index is that?" could not answer it from this page, which
                        is the question Ryan's team kept asking in the demo.
                        Flag OFF => `ppi_provenance` absent => the chip renders
                        exactly the string it does today.

                        Lane 825 adds the HOVER. Ryan: "they can hover over and
                        see exactly what each one of those things is... whatever
                        the most expanded definition of each one of these things
                        is." That is the COMPLETE published title, which the
                        backend ships in `ppi_provenance.titles` keyed by series
                        id -- fetched from the live series registry and pinned,
                        never constructed from the short name.

                        The chip keeps the SHORT name inline because it is a
                        pill in a wrapping row; the expanded definition is what
                        the hover is for. `title` is undefined when the flag is
                        off or the id has no fetched title, and React omits the
                        attribute entirely rather than rendering an empty
                        tooltip. */}
                    {priceImpact.ppi_blend.series_breakdown.map((s) => (
                      <span key={s.series_id} className="sd-blendchip"
                            title={priceImpact.ppi_blend.ppi_provenance?.titles?.[s.series_id]}>
                        {s.series_name}{priceImpact.ppi_blend.ppi_provenance ? ` (${s.series_id})` : ''}: {s.spend_weight_pct}%
                      </span>
                    ))}
                  </div>
                  {priceImpact.ppi_blend.ppi_provenance && (
                    /* Attribution from the backend (helpers/ppi_provenance.py),
                       so the copy cannot drift between the dossier, the deck
                       and chat. Lane 1432 (Ryan, 2026-10-27) removed the
                       selection disclosure that followed it. The hover hint is
                       the one string that is local, because hovering is a
                       property of this surface and no other one has it. */
                    <div className="sd-note" style={{ marginTop: '6px' }}>
                      Source: {priceImpact.ppi_blend.ppi_provenance.attribution}. Hover an index above for its full published series title.
                    </div>
                  )}
                  <div className="sd-note" style={{ marginTop: '6px' }}>
                    {priceImpact.ppi_blend.materials_included != null
                      ? `${priceImpact.ppi_blend.materials_included} of ${priceImpact.ppi_blend.materials_total} materials included in analysis (${priceImpact.ppi_blend.spend_coverage_pct}% of spend)${priceImpact.ppi_blend.materials_excluded > 0 ? `. ${priceImpact.ppi_blend.materials_excluded} excluded due to insufficient purchase history.` : ''}`
                      : `${priceImpact.ppi_blend.materials_mapped} of ${priceImpact.ppi_blend.materials_total} materials mapped to PPI indices`}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ---- Spend trend ---- */}
          {supplier.spend_trend?.length > 0 && (() => {
            const spendData = spendTimeframe === 0 ? supplier.spend_trend : supplier.spend_trend.slice(-spendTimeframe);
            return (
              <div className="sd-panel">
                <div className="sd-panel-h" style={{ alignItems: 'center' }}>
                  <span className="t">{spendTimeframe === 0 ? 'All-Time' : `Trailing ${spendTimeframe}-Month`} Spend</span>
                  <select className="sd-select" value={spendTimeframe} onChange={(e) => setSpendTimeframe(Number(e.target.value))} style={{ padding: '4px 8px', fontSize: '11px' }}>
                    <option value={6}>6 months</option>
                    <option value={12}>12 months</option>
                    <option value={24}>24 months</option>
                    <option value={0}>All time</option>
                  </select>
                </div>
                <div style={{ width: '100%', height: '220px', marginTop: '8px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartTimeData(spendData)} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
                      <defs>
                        <linearGradient id="spendGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={chartPalette[0]} stopOpacity={0.15} />
                          <stop offset="100%" stopColor={chartPalette[0]} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid {...chartGrid} />
                      <XAxis {...chartAxis} {...chartTimeAxis} ticks={chartTicks(spendData)} tickFormatter={chartMonth} />
                      <YAxis {...chartAxis} tickFormatter={chartMoney} width={60} />
                      <Tooltip formatter={(value) => [formatCurrency(value), 'Spend']} contentStyle={chartTooltipStyle} cursor={{ fill: 'rgba(34, 81, 255, 0.04)' }} />
                      <Area type="monotone" dataKey="spend" stroke={chartPalette[0]} strokeWidth={2} fill="url(#spendGradient)" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            );
          })()}
        </section>

        {/* ============ MATERIALS SUPPLIED ============ */}
        <section className="sd-sec">
          <div className="sd-sec-h">
            <span className="sd-lbl">Materials supplied</span>
            <span className="note">{filteredMaterials.length}</span>
            <span className="grow" />
            <input className="sd-input" type="text" placeholder="Search materials..." value={materialsSearch}
              onChange={(e) => { setMaterialsSearch(e.target.value); setMaterialsPage(1); }} style={{ width: '200px' }} />
            <select className="sd-select" value={materialsSortBy} onChange={(e) => { setMaterialsSortBy(e.target.value); setMaterialsPage(1); }}>
              <option value="spend">Sort: Spend</option>
              <option value="last_purchase">Sort: Last Purchase</option>
              <option value="material_number">Sort: Material #</option>
            </select>
          </div>

          {pagedMaterials.length > 0 ? (
            <>
              <DataTable className="sd-tbl">
                <thead>
                  <tr>
                    <th style={{ width: '42%' }}>Material</th>
                    <th style={{ width: '16%' }}>Category</th>
                    <th className="center" style={{ width: '12%' }}>Status</th>
                    <th className="right" style={{ width: '15%' }}>Spend (12mo)</th>
                    <th className="right" style={{ width: '15%' }}>Last Purchase</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedMaterials.map(mat => (
                    <tr key={mat.material_number} className="click" onClick={() => navigate(`/materials/${mat.material_number}`)}>
                      <td>
                        <span style={{ fontWeight: 600 }}>{mat.material_number}</span>
                        <span className="sd-muted" style={{ marginLeft: '8px' }}>{mat.material_name}</span>
                      </td>
                      <td className="sd-muted">{mat.category_label || mat.category || 'Not available'}</td>
                      <td className="center">
                        {mat.is_primary ? <span className="sd-badge good">Primary</span> : <span className="sd-badge mut">{mat.approval_status}</span>}
                      </td>
                      <td className="num"><Currency value={mat.spend_12mo}/></td>
                      <td className="right sd-muted">{formatDate(mat.last_activity)}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
              <Pager data={{page:materialsPage,total:filteredMaterials.length,total_pages:totalPages}} label="materials" onPage={setMaterialsPage}/>

            </>
          ) : (
            <div className="sd-empty">{materialsSearch ? 'No materials match your search' : 'No materials supplied'}</div>
          )}
        </section>




      </div>

      {/* Contract Detail Modal (reused; now reachable from the Contracts chip) */}
      {contractModalOpen && (
        <Modal
          isOpen={contractModalOpen}
          onClose={() => { setContractModalOpen(false); setContractModalData(null); setSelectedContract(null); }}
          title={`Contract: ${selectedContract?.contract_number || ''}`}
          size="medium"
        >
          {contractModalLoading ? (
            <div style={{ padding: '32px', textAlign: 'center' }}><LoadingSpinner text="Loading contract details..." /></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-3)', textTransform: 'uppercase', marginBottom: '4px' }}>Contract Number</div>
                  <div style={{ fontSize: '14px', fontWeight: 500 }}>{selectedContract?.contract_number || 'Not available'}</div>
                </div>
                {selectedContract?.contract_name && (
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-3)', textTransform: 'uppercase', marginBottom: '4px' }}>Contract Name</div>
                    <div style={{ fontSize: '14px' }}>{selectedContract.contract_name}</div>
                  </div>
                )}
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-3)', textTransform: 'uppercase', marginBottom: '4px' }}>Expiration Date</div>
                  <div style={{ fontSize: '14px', fontWeight: 500, color: (() => {
                    const days = getDaysUntilExpiry(selectedContract?.expiration_date);
                    if (days !== null && days <= 0) return 'var(--crit)';
                    if (days !== null && days <= 90) return 'var(--warn)';
                    return 'var(--text)';
                  })() }}>
                    {formatDate(selectedContract?.expiration_date)}
                    {(() => {
                      const days = getDaysUntilExpiry(selectedContract?.expiration_date);
                      if (days !== null && days > 0) return ` (${days} days)`;
                      if (days !== null && days <= 0) return ' (Expired)';
                      return '';
                    })()}
                  </div>
                </div>
                {selectedContract?.contracted_price && (
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-3)', textTransform: 'uppercase', marginBottom: '4px' }}>Contracted Price</div>
                    <div style={{ fontSize: '14px', fontWeight: 500, fontFamily: 'monospace' }}><Currency value={selectedContract.contracted_price} mode="unit-price"/></div>
                  </div>
                )}
              </div>

              {contractModalData ? (
                <>
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>Commercial Terms</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                      {contractModalData.pricing_mechanism && <div><div style={{ fontSize: '11px', color: 'var(--text-3)' }}>Pricing</div><div style={{ fontSize: '13px', fontWeight: 500 }}>{contractModalData.pricing_mechanism}</div></div>}
                      {contractModalData.payment_terms && <div><div style={{ fontSize: '11px', color: 'var(--text-3)' }}>Payment</div><div style={{ fontSize: '13px', fontWeight: 500 }}>{contractModalData.payment_terms}</div></div>}
                      {contractModalData.price_escalation_cap && <div><div style={{ fontSize: '11px', color: 'var(--text-3)' }}>Escalation Cap</div><div style={{ fontSize: '13px', fontWeight: 500 }}>{contractModalData.price_escalation_cap}%</div></div>}
                    </div>
                  </div>

                  {(contractModalData.auto_renewal != null || contractModalData.renewal_notice_days || contractModalData.renewal_term_months) && (
                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>Renewal Terms</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                        {contractModalData.auto_renewal != null && <div><div style={{ fontSize: '11px', color: 'var(--text-3)' }}>Auto-Renewal</div><div style={{ fontSize: '13px', fontWeight: 500 }}>{contractModalData.auto_renewal ? 'Yes' : 'No'}</div></div>}
                        {contractModalData.renewal_term_months && <div><div style={{ fontSize: '11px', color: 'var(--text-3)' }}>Renewal Term</div><div style={{ fontSize: '13px', fontWeight: 500 }}>{contractModalData.renewal_term_months} months</div></div>}
                        {contractModalData.renewal_notice_days && <div><div style={{ fontSize: '11px', color: 'var(--text-3)' }}>Notice Period</div><div style={{ fontSize: '13px', fontWeight: 500 }}>{contractModalData.renewal_notice_days} days</div></div>}
                      </div>
                    </div>
                  )}

                  {contractModalData.clauses && (() => {
                    const clauses = parseClauses(contractModalData.clauses);
                    const clauseEntries = Object.entries(clauses).filter(([, v]) => v && (v.summary || v.is_exclusive != null));
                    if (clauseEntries.length === 0) return null;
                    return (
                      <div style={{ borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>Key Clauses</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {clauseEntries.map(([key, clause]) => (
                            <div key={key} style={{ padding: '8px 12px', backgroundColor: 'var(--panel-2)', borderRadius: '6px' }}>
                              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', textTransform: 'capitalize', marginBottom: '2px' }}>
                                {key.replace(/_/g, ' ')}
                                {clause.is_exclusive != null && (
                                  <span style={{ marginLeft: '8px', fontSize: '10px', fontWeight: 600, padding: '1px 6px', borderRadius: '5px', color: clause.is_exclusive ? 'var(--crit)' : 'var(--good)', background: clause.is_exclusive ? 'color-mix(in srgb, var(--crit) 12%, transparent)' : 'color-mix(in srgb, var(--good) 12%, transparent)' }}>
                                    {clause.is_exclusive ? 'Exclusive' : 'Non-exclusive'}
                                  </span>
                                )}
                              </div>
                              {clause.summary && <div style={{ fontSize: '13px', color: 'var(--text-2)', lineHeight: 1.4 }}>{clause.summary}</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-3)', fontSize: '13px', borderTop: '1px solid var(--border)' }}>
                  No AI-parsed terms available for this contract.
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
                <button className="sd-btn" onClick={() => { setContractModalOpen(false); setContractModalData(null); setSelectedContract(null); }}>Close</button>
              </div>
            </div>
          )}
        </Modal>
      )}
    </PageShell>
  );
}

export default SupplierDetail;

export { formatCurrency as fmtCompactUsd } from '../utils/numberFormat';
